/**
 * A storage key that belongs to whoever is signed in, and so must not outlive them.
 *
 * Branded rather than a plain string so that `SESSION_STORAGE_KEYS` can only be built
 * from keys declared here. A `DevicePreferenceKey` -- the language, and anything else
 * that belongs to the browser rather than the account -- is a different type and does
 * not compile into that list. The decision is made once, at the declaration, next to the
 * comment explaining it, instead of being available at every call site forever after.
 *
 * The brand does not say a key is classified *correctly*: `sessionOwned()` is what
 * asserts that, and it is only as good as the person calling it. It says a preference
 * cannot join the teardown list by accident. `token.test.ts` covers the rest.
 */
declare const sessionOwnedBrand: unique symbol

export type SessionOwnedKey = string & { readonly [sessionOwnedBrand]: true }

const sessionOwned = (key: string) => key as SessionOwnedKey

export const TOKEN_KEY = sessionOwned("beringo.token")
/** The zustand persist key for the auth store. */
export const AUTH_STORAGE_KEY = sessionOwned("auth-storage")
export const ASSISTANT_PENDING_STOPS_KEY = sessionOwned(
  "assistant.pending-stops"
)
export const ASSISTANT_PENDING_DISPATCHES_KEY = sessionOwned(
  "assistant.pending-dispatches"
)
export const ASSISTANT_COMPLETION_NOTIFICATIONS_KEY = sessionOwned(
  "assistant.completion-notifications"
)
export const REVIEW_COMPLETION_NOTIFICATIONS_KEY = sessionOwned(
  "review.completion-notifications"
)
export const COMPLETION_NOTIFICATION_INBOX_KEY = sessionOwned(
  "completion.notification-inbox"
)
export const REFRESH_TOKEN_KEY = sessionOwned("beringo.token.refresh")
export const USER_ID = sessionOwned("userId")
export const GRADE_STATUS = sessionOwned("gradeStatus")
export const SUBSCRIPTION_MODE = sessionOwned("subscriptionMode")
export const ANONYMOUS_PROJECT_ID = sessionOwned("anonymousProjectId")
export const ANONYMOUS_USER_ID = sessionOwned("anonymousUserId")
export const PAYMENT_METHOD_CHANGE_PROCESS = sessionOwned(
  "paymentMethodChangeProcess"
)
export const ONLY_SUBSCRIBE_PROCESS = sessionOwned("onlySubscribeProcess")
export const INVALID_CARD_ERROR = sessionOwned("invalidCardError")
export const IS_API_MODE = sessionOwned("isApiMode")
export const TIER_ID = sessionOwned("tier")
export const ALREADY_ENTERED = sessionOwned("alreadyEntered")
export const IS_ALREADY_ONBOARDED_USER = sessionOwned("isAlreadyOnboardedUser")
export const LAST_TRANSLATION_FORM = sessionOwned("lastTranslationForm")
export const LAST_GLOSSARY_ID = sessionOwned("lastGlossaryId")
export const LAST_COUPON_CODE = sessionOwned("userId-couponCode")
export const IGNORE_LIMIT_WARNING = sessionOwned("ignoreLimitWarning")
export const IGNORE_QUOTA_WARNING = sessionOwned("ignoreQuotaWarning")
/** Whether downloads delete the source afterwards. */
export const IS_AUTO_DELETION_ENABLED = sessionOwned("isAutoDeletionEnabled")
export const IS_USER_ALREADY_CLOSED_SMART_GLOSSARY_DIALOG = sessionOwned(
  "isUserAlreadyClosedSmartGlossaryDialog"
)

export const createTokens = (token: string, refreshToken?: string) => {
  localStorage.setItem(TOKEN_KEY, token)
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

/**
 * Everything a session owns on this origin, as far as this list knows.
 *
 * Logout, a failed refresh and a password reset all reach removeTokens(). The auth
 * store subscribes to that signal to reset its in-memory state and query cache too.
 * Explicit logout still clears all of sessionStorage; classifying the remaining
 * session-owned keys is tracked in BeringLab/bering-console-v2#1653.
 *
 * `auth-storage` belongs here. Without it a reload restores `isAuthenticated: true`
 * from the persisted store while no token exists, and the user looks signed in while
 * every request fails.
 */
export const SESSION_STORAGE_KEYS: readonly SessionOwnedKey[] = [
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_STORAGE_KEY,
  ASSISTANT_PENDING_STOPS_KEY,
  ASSISTANT_PENDING_DISPATCHES_KEY,
  ASSISTANT_COMPLETION_NOTIFICATIONS_KEY,
  REVIEW_COMPLETION_NOTIFICATIONS_KEY,
  COMPLETION_NOTIFICATION_INBOX_KEY,
  // Everything below describes the person who was signed in, so it must not be read by
  // whoever signs in next. `localStorage.clear()` used to take all of it by accident;
  // logout()'s hand-written list did not, which is how ALREADY_ENTERED could suppress a
  // new account's onboarding banner because the previous one had projects.
  GRADE_STATUS,
  SUBSCRIPTION_MODE,
  TIER_ID,
  IS_API_MODE,
  ALREADY_ENTERED,
  IS_ALREADY_ONBOARDED_USER,
  IS_USER_ALREADY_CLOSED_SMART_GLOSSARY_DIALOG,
  LAST_TRANSLATION_FORM,
  LAST_GLOSSARY_ID,
  LAST_COUPON_CODE,
  IGNORE_LIMIT_WARNING,
  IGNORE_QUOTA_WARNING,
  // Not a preference like the theme: left behind it deletes the next account's file
  // after they download it, having never been switched on by them.
  IS_AUTO_DELETION_ENABLED,
  // Mid-flow markers. Left behind they resume somebody else's checkout.
  PAYMENT_METHOD_CHANGE_PROCESS,
  ONLY_SUBSCRIBE_PROCESS,
  INVALID_CARD_ERROR,
]

const sessionRemovalListeners = new Set<() => void>()

/** Session work held in memory must also end on refresh failure and password reset. */
export const onSessionRemoved = (listener: () => void) => {
  sessionRemovalListeners.add(listener)
  return () => {
    sessionRemovalListeners.delete(listener)
  }
}

/**
 * Removes the session, and only the session.
 *
 * It was `localStorage.clear()`, which also took the theme, the language and every
 * other preference on the origin -- none of which belongs to whoever was signed in.
 *
 * Both storages, because these keys do not agree on one. The subscription flow keeps
 * GRADE_STATUS, IS_API_MODE, PAYMENT_METHOD_CHANGE_PROCESS and INVALID_CARD_ERROR in
 * sessionStorage only, while TIER_ID and ONLY_SUBSCRIBE_PROCESS are written to both.
 * Clearing one storage would leave the other half of a checkout for the next person to
 * sign in to resume. Removing a key that was never there costs nothing, so the list
 * stays single and is applied to both rather than split into two that can drift.
 */
export const removeTokens = () => {
  sessionRemovalListeners.forEach((listener) => listener())
  SESSION_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })
}

export const getAccessToken = () => {
  return localStorage.getItem(TOKEN_KEY) || ""
}

export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY) || ""
}
